import React, { useState } from "react";
import {
  ControlPosition,
  MapControl,
  useMap,
  useMapsLibrary,
} from "@vis.gl/react-google-maps";
import { useEffect } from "react";
import {
  LocationClusterType,
  useGetLocationsQuery,
} from "../store/slices/mapsSlice";
import { convertMapBoundsToGeoJson } from "./helpers";
import MapSelection from "./MapSelection";
import MapSidebar from "./MapSidebar";
import ContactsIcon from "../../assets/icons/contact_edit.svg2";
import { createRoot } from "react-dom/client";
import { MapFilterType } from "./MapFilters";
import DefaultAssetIcon from "../../assets/icons/Icon_Library/application icons/assets/events/asset_events/asset.svg2";
import { Form, Overlay, Popover } from "react-bootstrap";
import {
  AssetOverlay,
  ClusterPopUp,
  ContactOverlay,
  EventOverlay,
} from "./MapPopUps";
import AssetsIcon from "../Assets/AssetsIcon";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faX } from "@fortawesome/free-solid-svg-icons";

const ClusterIconOverlay = ({
  marker: m,
  filters,
  hideEvents,
}: {
  marker:
    | {
        cluster: LocationClusterType;
        content: HTMLDivElement;
      }
    | undefined;
  filters?: MapFilterType;
  hideEvents?: boolean;
}) => {
  const [marker, setMarker] = useState<{
    cluster: LocationClusterType;
    content: HTMLDivElement;
  }>();
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (m) {
      setMarker(m);
      setShow(true);
    }
  }, [m]);
  const { cluster, content } = marker || {};

  return (
    content &&
    cluster && (
      <Overlay
        show={show}
        target={content}
        placement="auto"
        flip
        rootClose
        onHide={() => {
          setShow(false);
        }}
      >
        {(props: any) => (
          <Popover className="bg-purple-gradient" {...props}>
            <button
              className="btn btn-outline-secondary border-0 float-end btn-sm"
              onClick={() => {
                setShow(false);
              }}
            >
              <FontAwesomeIcon icon={faX} />
            </button>
            {/* <Popover.Header as="h3"> details</Popover.Header> */}
            <Popover.Body>
              {cluster.IsCluster && <ClusterPopUp cluster={cluster} />}
              {cluster.Item?.ContactUuid && (
                <ContactOverlay contactUuid={cluster.Item.ContactUuid} />
              )}
              {cluster.Item?.ContactUuid && cluster.Item?.AssetUuid && <hr />}
              {cluster.Item?.AssetUuid && (
                <AssetOverlay
                  assetUuid={cluster.Item.AssetUuid}
                  assetOnly={filters?.Assets && !filters?.Contacts}
                  showEvents={filters?.OnlyItemsWithEvents && !hideEvents}
                  cluster={cluster}
                />
              )}
              {cluster.Item?.EventUuid && (
                <div>
                  <EventOverlay eventUuid={cluster.Item.EventUuid} />
                </div>
              )}
            </Popover.Body>
          </Popover>
        )}
      </Overlay>
    )
  );
};

export default function Clusters({
  // setOverlay,
  filters,
  setLoading,
  onLoad,
  showSelection,
  showPopup,
  setOverlay,
  hideSidebar,
  onSelect,
  hideEvents,
}: {
  // setOverlay: any;
  filters?: MapFilterType;
  setLoading?: (isLoading: boolean) => void;
  onLoad?: (clusters: any[]) => void;
  showSelection?: boolean;
  showPopup?: (o: any, e: any, map: any) => void;
  setOverlay?: any;
  hideSidebar?: boolean;
  onSelect?: (cluster: LocationClusterType) => void;
  hideEvents?: boolean;
}) {
  const [selectedMarker, setSelectedMarker] = useState<{
    cluster: LocationClusterType;
    content: HTMLDivElement;
  }>();
  const map = useMap();
  const marker = useMapsLibrary("marker");
  const mapGeometry = useMapsLibrary("geometry");
  const [mapBounds, setMapBounds] = useState<any[][]>();
  const [debouncedMapBounds, setDebouncedMapBounds] = useState<any[][]>();
  const [clusteringRadius, setClusteringRadius] = useState(0);
  const [debouncedZoom, setDebouncedZoom] = useState(0);
  const [pagination, setPagination] = useState({
    offset: 0,
    limit: 1000,
  });
  const [hoveredShapeUuid, setHoveredShapeUuid] = useState<string>();

  useEffect(() => {
    const t = setTimeout(() => {
      if (mapBounds) {
        setDebouncedMapBounds([[mapBounds]]);
      }
      setDebouncedZoom(map?.getZoom() || 10);
      setClusteringRadius(
        map && map.getBounds() && mapGeometry
          ? mapGeometry?.spherical.computeDistanceBetween(
              map?.getBounds()?.getNorthEast() as google.maps.LatLng,
              map?.getBounds()?.getSouthWest() as google.maps.LatLng
            ) / 40000
          : 0
      );
    }, 700);

    return () => {
      clearTimeout(t);
    };
  }, [mapBounds]);

  const [searchArea, setSearchArea] = useState<any[][]>();
  const handleSetSearchArea = (area: any) => {
    setSearchArea(area ? [[area]] : undefined);
  };

  const {
    data: { items: clusters, total } = { items: [], total: 0 },
    isFetching,
    isLoading,
  } = useGetLocationsQuery(
    {
      ...filters,
      Coordinates: searchArea || debouncedMapBounds,
      Limit: pagination.limit,
      Offset: pagination.offset,
      Radius: clusteringRadius,
    },
    { skip: !map || !(searchArea || debouncedMapBounds) }
  );

  useEffect(() => {
    if (!setLoading) return;

    if (isFetching || isLoading) {
      setLoading(true);
    } else {
      setTimeout(() => {
        setLoading(false);
      }, 300);
    }
  }, [isFetching, isLoading, setLoading]);

  function showClusters({
    clusters,
    map,
    marker,
  }: {
    clusters: LocationClusterType[] | undefined;
    map: google.maps.Map;
    marker: google.maps.MarkerLibrary | null;
  }) {
    const locationShapes: any[] = [];

    if (!map || !marker) return;

    clusters?.forEach((cluster) => {
      if (
        cluster.Item?.IsShape &&
        hoveredShapeUuid === (cluster.Item?.Uuid || "")
      ) {
        const polygon = new google.maps.Polygon({
          paths: cluster.Item.Area?.coordinates[0][0].map(([lng, lat]) => ({
            lat,
            lng,
          })),
        });
        polygon.setMap(map);
        // polygon.addListener("click", (e: any) => {
        //   showPopup?.(cluster, e, map);
        // });
        // (polygon as any).clusterData = cluster;
        locationShapes.push(polygon);
      }

      const markerElement = document.createElement("div");
      const root = createRoot(markerElement);

      root.render(
        <div
          className={`p-1 rounded-circle text-white position-relative d-flex align-items-center justify-content-center`}
          style={{
            width: 30,
            height: 30,
            backgroundColor: cluster.ActiveEventsSeverity?.critical
              ? "red"
              : cluster.ActiveEventsSeverity?.high
              ? "orange"
              : cluster.ActiveEventsSeverity?.medium
              ? "yellow"
              : cluster.ActiveEventsSeverity?.low
              ? "green"
              : "black",
          }}
          onMouseEnter={() => {
            setSelectedMarker({
              cluster,
              content: markerElement,
            });
            setHoveredShapeUuid(
              cluster.Item?.IsShape ? cluster.Item?.Uuid || "" : undefined
            );
          }}
          onMouseLeave={() => {
            setSelectedMarker(undefined);
            setHoveredShapeUuid(undefined);
          }}
        >
          {cluster.IsCluster ? (
            <div className="text-center">{cluster.ItemCount}</div>
          ) : cluster.Item?.ContactUuid ? (
            <ContactsIcon className="white" />
          ) : (
            <AssetsIcon
              assetKind={cluster.Item?.Asset?.Kind}
              white={
                !cluster.ActiveEventsSeverity?.medium &&
                !cluster.ActiveEventsSeverity?.low
              }
              width={24}
            />
          )}
        </div>
      );
      const m = new marker.AdvancedMarkerElement({
        position: {
          lat: cluster.Y ?? 0,
          lng: cluster.X ?? 0,
        },
        content: markerElement,
        map,
      });

      if ((cluster as LocationClusterType).IsCluster) {
        m.element.addEventListener("gmp-click", (e: any) => {
          let bounds = new google.maps.LatLngBounds();
          var east = google.maps.geometry.spherical.computeOffset(
            {
              lat: (cluster as LocationClusterType).Y,
              lng: (cluster as LocationClusterType).X,
            },
            clusteringRadius * 1000,
            90
          );
          bounds.extend(east);
          var west = google.maps.geometry.spherical.computeOffset(
            {
              lat: (cluster as LocationClusterType).Y,
              lng: (cluster as LocationClusterType).X,
            },
            clusteringRadius * 1000,
            270
          );
          bounds.extend(west);

          map.fitBounds(bounds);
        });
      } else {
        if (showPopup) {
          m.element.addEventListener("gmp-click", (e: any) => {
            showPopup?.(cluster, e, map);
          });
        }
      }
      (m as any).clusterData = cluster;
      locationShapes.push(m);
      // }
    });

    return locationShapes;
  }

  useEffect(() => {
    if (!map) return;

    const boundChageEvent = map.addListener("idle", () => {
      setMapBounds(convertMapBoundsToGeoJson(map));
    });

    return () => {
      google.maps.event.removeListener(boundChageEvent);
    };
  }, [map]);

  useEffect(() => {
    if (!map || !marker) return;

    const locationShapes: any[] =
      showClusters({
        clusters,
        map,
        marker,
      }) || [];

    onLoad?.(locationShapes);

    return () => {
      locationShapes.forEach((shape) => {
        shape.setMap?.(null);
        shape.map = null;
      });
    };
  }, [map, clusters, hoveredShapeUuid]);

  const [showSidebar, setShowSidebar] = useState<{
    show: boolean;
    bounds: any;
  }>({ show: false, bounds: null });

  return (
    <>
      <ClusterIconOverlay
        marker={selectedMarker}
        filters={filters}
        hideEvents={hideEvents}
      />
      {showSelection && (
        <MapSelection
          drawingModes={["polygon", "rectangle"]}
          setOverlay={setOverlay}
          setMapBounds={handleSetSearchArea}
          setShowSidebar={setShowSidebar}
          onSelect={onSelect}
        />
      )}
      {!hideSidebar && (
        <MapControl position={ControlPosition.TOP_RIGHT}>
          <div className="d-flex p-2 m-1 justify-content-end bg-light align-items-center fw-bold">
            {total} locations found.
            {total > pagination.offset + pagination.limit && (
              <>
                Showing {pagination.offset + 1} to{" "}
                {Math.min(pagination.offset + pagination.limit, total)}
                <button
                  className="btn btn-link p-0 ms-2"
                  disabled={pagination.offset - pagination.limit < 0}
                  onClick={() =>
                    setPagination((p) => ({
                      limit: p.limit,
                      offset: p.offset - p.limit,
                    }))
                  }
                >
                  Prev
                </button>
                <button
                  className="btn btn-link p-0 ms-2"
                  disabled={pagination.offset + pagination.limit > total}
                  onClick={() =>
                    setPagination((p) => ({
                      limit: p.limit,
                      offset: p.offset + p.limit,
                    }))
                  }
                >
                  Next
                </button>
              </>
            )}
            <MapSidebar
              show={showSidebar.show}
              setShow={(newState) =>
                setShowSidebar({
                  show: newState,
                  bounds: debouncedMapBounds,
                })
              }
              bounds={showSidebar.bounds}
              filters={filters}
            />
          </div>
        </MapControl>
      )}
    </>
  );
}
